import React from 'react';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface LocationPreviewProps {
  /**
   * Provider location (e.g., "Brussel, België")
   */
  location: string;
  /**
   * Full address if available
   */
  address?: string;
  /**
   * Service type to determine display
   */
  serviceType?: 'online' | 'in-person' | 'both';
  /**
   * Compact mode for marketplace listings
   */
  compact?: boolean;
  /**
   * Click handler for location click
   */
  onClick?: () => void;
}

const LocationPreview: React.FC<LocationPreviewProps> = ({ 
  location, 
  address, 
  serviceType = 'in-person',
  compact = false,
  onClick
}) => {
  const destination = address || location;
  const encodedDestination = encodeURIComponent(destination);

  const openGoogleMaps = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `https://www.google.com/maps/search/?api=1&query=${encodedDestination}`;
    window.open(url, '_blank');
    toast.success('Google Maps geopend');
  };

  // Online-only services
  if (serviceType === 'online') {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-xs" style={{ 
          backgroundColor: '#efe7dd', 
          color: '#a0897c' 
        }}>
          🌐 Online
        </Badge>
        {!compact && (
          <span className="text-xs" style={{ color: '#b6a092' }}>
            Geen fysieke locatie
          </span>
        )}
      </div>
    );
  }

  // Compact view for marketplace
  if (compact) {
    return (
      <div 
        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={onClick}
      >
        <MapPin className="h-3 w-3" style={{ color: '#b6a092' }} />
        <span className="text-xs truncate" style={{ color: '#a0897c' }}>
          {location}
        </span>
        {serviceType === 'both' && (
          <Badge variant="secondary" className="text-xs ml-auto" style={{ 
            backgroundColor: '#fbf8f4', 
            color: '#b6a092' 
          }}>
            Hybrid
          </Badge>
        )}
      </div>
    );
  }

  // Full preview with navigation
  return (
    <div className="rounded-lg p-3 border" style={{ 
      backgroundColor: '#fbf8f4', 
      borderColor: '#efe7dd'
    }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" style={{ color: '#b6a092' }} />
          <div>
            <p className="text-sm font-medium" style={{ color: '#837069' }}>
              {location}
            </p>
            {address && address !== location && (
              <p className="text-xs" style={{ color: '#b6a092' }}>
                {address}
              </p>
            )}
          </div>
        </div>
        
        <Button
          size="sm"
          variant="outline"
          onClick={openGoogleMaps}
          className="text-xs hover:bg-beige-100"
          style={{ 
            borderColor: '#e6dbd0',
            color: '#837069'
          }}
        >
          <Navigation className="h-3 w-3 mr-1" />
          Route
          <ExternalLink className="h-3 w-3 ml-1" />
        </Button>
      </div>

      {serviceType === 'both' && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs" style={{ 
            backgroundColor: '#efe7dd', 
            color: '#a0897c' 
          }}>
            Online & Op locatie
          </Badge>
          <span className="text-xs" style={{ color: '#b6a092' }}>
            Flexibele afspraak opties
          </span>
        </div>
      )}

      {/* Quick Belgium context */}
      <div className="mt-2 pt-2 border-t border-stone-200">
        <div className="flex items-center justify-between text-xs">
          <span style={{ color: '#b6a092' }}>🇧🇪 België</span>
          <div className="flex items-center gap-3" style={{ color: '#b6a092' }}>
            <span>🚗 Parkeren mogelijk</span>
            <span>🚌 OV beschikbaar</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPreview;